import { TaxCode, TaxRegion } from "@/Contracts/Users";
import { capitalizeFirstLetter } from "@/Contracts/Utils";
import { ColumnProps } from "antd/lib/table";
import React, { ReactNode } from "react";
import FormBuilder from "antd-form-builder";
import { FormInstance } from "antd/lib/form";
import { EWidgets } from "@organisms/ZForm/interface";
import { InputNumberWidths } from "@atoms/ZInputNumber";
import ZIcon, { Icons } from "@atoms/ZIcon";
import Row from "antd/lib/row";
import Col from "antd/lib/col";
import "./index.less";

export const getTaxCategoryColumns = (
	taxCodes: TaxCode[],
	form: FormInstance<any>,
	isLoading: boolean,
	addTaxCodeLevel: (region: TaxRegion) => void,
	editTaxCodeLevelName: (
		taxCodeName: string,
		region: TaxRegion,
		taxCodeLevelIndex: number
	) => void,
	editTaxCodeLevelRate: (
		taxCodeRate: number,
		region: TaxRegion,
		taxCodeLevelIndex: number
	) => void,
	removeTaxCodeLevel: (region: TaxRegion, taxCodeLevelIndex: number) => void
): ColumnProps<TaxRegion>[] => [
	{
		title: "REGION",
		dataIndex: "name",
		key: "name",
		render: (_text: unknown, record: TaxRegion): ReactNode => {
			return record.disabled
				? `${capitalizeFirstLetter(record.name)} (Inactive)`
				: capitalizeFirstLetter(record.name);
		}
	},
	{
		title: "TAX NAME",
		dataIndex: "tax",
		key: "tax",
		render: (_text: unknown, record: TaxRegion): ReactNode => {
			const levels = taxCodes.find(
				taxCode => taxCode.region?.id === record.id
			)?.levels;
			return (
				<div>
					{levels?.map((level, index) => (
						<div
							className="tax-category-name-form-item"
							key={`tax_name_${record.id}_${index}`}
						>
							<FormBuilder
								form={form}
								meta={{
									fields: [
										{
											wrapperCol: { span: 24 },
											key: `tax_codes.${record.id}.${index}.name`,
											required: true,
											message: "Please enter tax name",
											rules: [
												{
													whitespace: true,
													message:
														"Please enter tax name"
												}
											],
											initialValue:
												level?.name?.length > 0
													? level.name
													: index === 0
													? `${capitalizeFirstLetter(
															record.name
													  )} Tax`
													: undefined,
											widget: EWidgets.INPUT,
											widgetProps: {
												id: `tax_codes_${record.id}_${index}_name`,
												placeholder:
													"Please enter tax name",
												onChange: (
													event: React.ChangeEvent<HTMLInputElement>
												) =>
													editTaxCodeLevelName(
														event.target.value,
														record,
														index
													),
												disabled:
													isLoading ||
													record.disabled,
												width: "full"
											}
										}
									]
								}}
							/>
						</div>
					))}
				</div>
			);
		}
	},
	{
		title: "TAX RATE",
		dataIndex: "rate",
		key: "rate",
		width: 150,
		render: (_text: unknown, record: TaxRegion): ReactNode => {
			const levels = taxCodes.find(
				taxCode => taxCode.region?.id === record.id
			)?.levels;
			return (
				<div>
					{levels?.map((level, index) => (
						<div
							className="mt-2 mb-n4"
							key={`tax_rate_${record.id}_${index}`}
						>
							<FormBuilder
								form={form}
								meta={{
									fields: [
										{
											wrapperCol: { span: 24 },
											key: `tax_codes.${record.id}.${index}.tax_percentage`,
											required: true,
											message: "Please enter tax rate",
											initialValue:
												level?.tax_percentage ?? 0,
											widget: EWidgets.INPUTNUMBER,
											widgetProps: {
												id: `tax_codes_${record.id}_${index}_tax_percentage`,
												placeholder:
													"Please enter tax rate",
												disabled:
													isLoading ||
													record.disabled,
												addOnAfter: "%",
												min: 0,
												max: 100,
												step: ".001",
												width: InputNumberWidths.lg,
												precision: 3,
												onChange: (value: number) =>
													editTaxCodeLevelRate(
														value || 0,
														record,
														index
													)
											}
										}
									]
								}}
							/>
						</div>
					))}
				</div>
			);
		}
	},
	{
		title: "Action",
		dataIndex: "actions",
		key: "actions",
		width: 80,
		render: (_text: unknown, record: TaxRegion): ReactNode => {
			const levels = taxCodes.find(
				taxCode => taxCode.region?.id === record.id
			)?.levels;
			return (
				!record.disabled && (
					<div
						className="grid gap-y-5"
						key={`tax_actions_${record.id}`}
					>
						{levels?.map((_level, index) => (
							<Row
								className="items-center"
								key={`tax_actions_${record.id}_${index}`}
							>
								<Col span={12}>
									<ZIcon
										name={Icons.ADD}
										size="sm"
										data-testid="add-tax-level-icon"
										onClick={() =>
											index === levels.length - 1 &&
											addTaxCodeLevel(record)
										}
										colorVariant={7}
										className={`${
											index === levels.length - 1
												? "cursor-pointer"
												: "opacity-0"
										}`}
									/>
								</Col>
								<Col span={12}>
									<ZIcon
										name={Icons.TRASH}
										size="sm"
										data-testid="delete-tax-level-icon"
										onClick={() =>
											index > 0 &&
											removeTaxCodeLevel(record, index)
										}
										colorVariant={7}
										className={`${
											index > 0
												? "cursor-pointer"
												: "opacity-0"
										}`}
									/>
								</Col>
							</Row>
						))}
					</div>
				)
			);
		}
	}
];
